import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { fetchDealerConfig } from '@/features/dealers/store/dealerConfigSlice';
import { useSingleVisibleDealer } from '@/features/dealers/hooks/useSingleVisibleDealer';
import { resolveDeferDealerSelection } from '@/features/dealers/utils/resolveDeferDealerSelection';
import { resolveStepOrder } from '../services/quoteStepOrder';
import type { Dealer } from '@/features/dealers/types/dealers.types';
import type { QuoteStep } from '../types/quote.types';

/**
 * Resolve the quote journey's step order (Epic 7.1.8). HOME DEALER ONLY —
 * resolving from the selected dealer would flip the order mid-journey and misroute nextStep().
 */
export function useQuoteStepOrder(): {
    order: readonly QuoteStep[];
    defer: boolean;
    settled: boolean;
    autoDealer: Dealer | null;
} {
    const dispatch = useAppDispatch();
    const homeDealerId = useAppSelector((s) => s.auth.dealerId);
    const settings = useAppSelector((s) =>
        (homeDealerId ? s.dealerConfig.byDealerId[homeDealerId] : undefined));
    const status = useAppSelector((s) =>
        (homeDealerId ? s.dealerConfig.status[homeDealerId] : undefined)) ?? 'idle';
    const singleDealer = useSingleVisibleDealer();

    useEffect(() => {
        // Self-heal: the login prefetch doesn't cover the AuthProvider startup-refresh path.
        if (homeDealerId && status === 'idle') {
            void dispatch(fetchDealerConfig(homeDealerId));
        }
    }, [dispatch, homeDealerId, status]);

    const defer = resolveDeferDealerSelection(settings);
    const configSettled = homeDealerId === null || status === 'ready' || status === 'error';
    return {
        // A user who can see exactly one dealer skips the dealer step; that dealer is `autoDealer`.
        order: resolveStepOrder(defer, singleDealer.dealer !== null),
        defer,
        // 'idle' is permanent for a user with no home dealer.
        settled: configSettled && singleDealer.settled,
        autoDealer: singleDealer.dealer,
    };
}
